import { PlayerServiceAPI } from '@API/data-services/player-service'
import {
  AuthScopes,
  PamPlayerVerificationSummary,
  PlayerWithPAMDetails,
  ResponseError,
} from '@Generated/back-office-client/src'
import { Badge, Button, Callout, Card, Flex, Grid, Text, TextField } from '@radix-ui/themes'
import { ComponentProps, useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useMutation } from '@tanstack/react-query'
import { RootState } from '@AppState/redux-store'
import { usePlayerCapabilities } from './use-player-capabilities'
import { WithPermissions } from '@Components/common/with-permissions'
import { getStatusDisplay } from '@Components/pam-verification/pam-verification-status-display'
import {
  getAccountStatusDisplay,
  getBannerDisplay,
} from '@Components/pam-verification/pam-player-status-display'
import { Routes } from '@Components/main-app/types'
import { CheckCircledIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons'

type PlayerDetailsOutletContext = {
  playerDetails: PlayerWithPAMDetails | null
  onDetailsUpdated: (details: PlayerWithPAMDetails) => void
}

const DetailField = ({
  label,
  value,
  editable,
  onChange,
  type,
}: {
  label: string
  value?: string | null
  editable?: boolean
  onChange?: (value: string) => void
  type?: ComponentProps<typeof TextField.Root>['type']
}) => (
  <Flex direction="column" gap="1">
    <Text as="label" weight="bold" size="2">
      {label}
    </Text>
    {editable ? (
      <TextField.Root
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
      />
    ) : (
      <Text>{value ?? 'N/A'}</Text>
    )}
  </Flex>
)

const KycStatusBanner = ({ verification }: { verification: PamPlayerVerificationSummary }) => {
  const nav = useNavigate()
  const banner = getBannerDisplay(verification.accountStatus, verification.status)
  const accountDisplay = getAccountStatusDisplay(verification.accountStatus)
  const verificationDisplay = getStatusDisplay(verification.status)
  const Icon = banner.attention ? ExclamationTriangleIcon : CheckCircledIcon
  return (
    <Callout.Root
      color={banner.color}
      variant="soft"
      style={{ display: 'flex', alignItems: 'center' }}>
      <Flex align="center" justify="between" gap="3" width="100%">
        <Flex align="center" gap="2">
          <Icon />
          <Text size="2" weight="bold">
            Account
          </Text>
          <Badge color={accountDisplay.color} variant="soft">
            {accountDisplay.label}
          </Badge>
          <Text size="2">·</Text>
          <Text size="2">Verification</Text>
          <Badge color={verificationDisplay.color} variant="soft">
            {verificationDisplay.label}
          </Badge>
        </Flex>
        <Button
          color={banner.color}
          onClick={() =>
            nav(Routes.PAM_VERIFICATION_DETAIL.replace(':pamPlayerId', verification.pamPlayerId))
          }>
          See Details
        </Button>
      </Flex>
    </Callout.Root>
  )
}

export const PlayerDetails = () => {
  const params = useParams()
  const ctx = useOutletContext<PlayerDetailsOutletContext>()

  const playerLookupId = params?.lookupId
  const playerDetails = playerLookupId ? ctx.playerDetails : null

  const { data: capabilities } = usePlayerCapabilities()
  const isEditable = capabilities?.isEditable === true
  const { currentUser } = useSelector((state: RootState) => state.users)

  const details = playerDetails?.details
  const [isEditing, setIsEditing] = useState(false)
  const [firstName, setFirstName] = useState(details?.firstName ?? '')
  const [lastName, setLastName] = useState(details?.lastName ?? '')
  const [email, setEmail] = useState(details?.email ?? '')
  const [phone, setPhone] = useState(details?.phone ?? '')
  const [address, setAddress] = useState(details?.address ?? '')
  const [addressLine2, setAddressLine2] = useState(details?.addressLine2 ?? '')
  const [city, setCity] = useState(details?.city ?? '')
  const [region, setRegion] = useState(details?.region ?? '')
  const [postalCode, setPostalCode] = useState(details?.postalCode ?? '')
  const [dob, setDob] = useState(details?.dob ?? '')
  const [ssn4, setSsn4] = useState(details?.ssn4 ?? '')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    setFirstName(details?.firstName ?? '')
    setLastName(details?.lastName ?? '')
    setEmail(details?.email ?? '')
    setPhone(details?.phone ?? '')
    setAddress(details?.address ?? '')
    setAddressLine2(details?.addressLine2 ?? '')
    setCity(details?.city ?? '')
    setRegion(details?.region ?? '')
    setPostalCode(details?.postalCode ?? '')
    setDob(details?.dob ?? '')
    setSsn4(details?.ssn4 ?? '')
  }, [
    details?.firstName,
    details?.lastName,
    details?.email,
    details?.phone,
    details?.address,
    details?.addressLine2,
    details?.city,
    details?.region,
    details?.postalCode,
    details?.dob,
    details?.ssn4,
  ])

  const { mutate: save, isPending } = useMutation({
    mutationFn: async () => {
      setErrorMessage(null)
      if (!playerDetails) {
        return
      }
      const request: {
        firstName?: string
        lastName?: string
        email?: string
        phone?: string
        address?: string
        addressLine2?: string
        city?: string
        region?: string
        postalCode?: string
        dob?: string
        ssn4?: string
      } = {}
      if (firstName !== (details?.firstName ?? '')) {
        request.firstName = firstName
      }
      if (lastName !== (details?.lastName ?? '')) {
        request.lastName = lastName
      }
      if (email !== (details?.email ?? '')) {
        request.email = email
      }
      if (phone !== (details?.phone ?? '')) {
        request.phone = phone
      }
      if (address !== (details?.address ?? '')) {
        request.address = address
      }
      if (addressLine2 !== (details?.addressLine2 ?? '')) {
        request.addressLine2 = addressLine2
      }
      if (city !== (details?.city ?? '')) {
        request.city = city
      }
      if (region !== (details?.region ?? '')) {
        request.region = region
      }
      if (postalCode !== (details?.postalCode ?? '')) {
        request.postalCode = postalCode
      }
      if (dob !== (details?.dob ?? '')) {
        request.dob = dob
      }
      if (ssn4 !== (details?.ssn4 ?? '')) {
        request.ssn4 = ssn4
      }
      const updated = await PlayerServiceAPI.updatePlayerDetails(
        playerDetails.player.lookupId,
        request,
      )
      ctx.onDetailsUpdated(updated)
    },
    onSuccess: () => {
      setIsEditing(false)
    },
    onError: async (e) => {
      if (e instanceof ResponseError) {
        setErrorMessage((await e.response.json()).message)
      } else {
        setErrorMessage('Failed to save changes.')
      }
    },
  })

  if (!playerDetails || !details) {
    return null
  }

  const cancel = () => {
    setFirstName(details.firstName ?? '')
    setLastName(details.lastName ?? '')
    setEmail(details.email ?? '')
    setPhone(details.phone ?? '')
    setAddress(details.address ?? '')
    setAddressLine2(details.addressLine2 ?? '')
    setCity(details.city ?? '')
    setRegion(details.region ?? '')
    setPostalCode(details.postalCode ?? '')
    setDob(details.dob ?? '')
    setSsn4(details.ssn4 ?? '')
    setErrorMessage(null)
    setIsEditing(false)
  }

  const hasChanges =
    firstName !== (details.firstName ?? '') ||
    lastName !== (details.lastName ?? '') ||
    email !== (details.email ?? '') ||
    phone !== (details.phone ?? '') ||
    address !== (details.address ?? '') ||
    addressLine2 !== (details.addressLine2 ?? '') ||
    city !== (details.city ?? '') ||
    region !== (details.region ?? '') ||
    postalCode !== (details.postalCode ?? '') ||
    dob !== (details.dob ?? '') ||
    ssn4 !== (details.ssn4 ?? '')

  const addressEditable = Boolean(details.address)
  const identityEditable = Boolean(details.dob) || Boolean(details.ssn4)
  const canWriteSsn = currentUser?.scopes.includes(AuthScopes.writeplayer_ssn) ?? false

  return (
    <Flex direction="column" gap="4">
      <Card className="player-external-details">
        <Flex direction="column" gap="5">
          {isEditable && (
            <Flex justify="end" gap="2">
              {isEditing ? (
                <>
                  <Button variant="soft" color="gray" onClick={cancel} disabled={isPending}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => save()}
                    disabled={!hasChanges || isPending}
                    loading={isPending}>
                    Save
                  </Button>
                </>
              ) : (
                <Button variant="soft" color="gray" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
            </Flex>
          )}

          <Grid columns="4" gapX="6" gapY="5">
            <DetailField
              label="First name"
              value={isEditing ? firstName : details.firstName}
              editable={isEditing}
              onChange={setFirstName}
            />
            <DetailField
              label="Last name"
              value={isEditing ? lastName : details.lastName}
              editable={isEditing}
              onChange={setLastName}
            />
            <DetailField
              label="Email"
              value={isEditing ? email : details.email}
              editable={isEditing}
              onChange={setEmail}
            />
            <DetailField
              label="Phone"
              value={isEditing ? phone : details.phone}
              editable={isEditing}
              onChange={setPhone}
            />
            <DetailField
              label="Address"
              value={isEditing ? address : details.address}
              editable={isEditing && addressEditable}
              onChange={setAddress}
            />
            <DetailField
              label="Address line 2"
              value={isEditing ? addressLine2 : details.addressLine2}
              editable={isEditing && addressEditable}
              onChange={setAddressLine2}
            />
            <DetailField
              label="City"
              value={isEditing ? city : details.city}
              editable={isEditing && addressEditable}
              onChange={setCity}
            />
            <DetailField
              label="State"
              value={isEditing ? region : details.region}
              editable={isEditing && addressEditable}
              onChange={setRegion}
            />
            <DetailField
              label="Zip"
              value={isEditing ? postalCode : details.postalCode}
              editable={isEditing && addressEditable}
              onChange={setPostalCode}
            />
            <DetailField
              label="Date of birth"
              type="date"
              value={isEditing ? dob : details.dob}
              editable={isEditing && identityEditable}
              onChange={setDob}
            />
            <WithPermissions requiredPermissions={AuthScopes.readplayer_ssn}>
              <DetailField
                label="SSN (last 4)"
                value={isEditing ? ssn4 : details.ssn4}
                editable={isEditing && identityEditable && canWriteSsn}
                onChange={setSsn4}
              />
            </WithPermissions>
          </Grid>

          {errorMessage ? <Text color="red">{errorMessage}</Text> : null}
        </Flex>
      </Card>
      {playerDetails.verification ? (
        <KycStatusBanner verification={playerDetails.verification} />
      ) : null}
    </Flex>
  )
}
